import React, { useEffect, useCallback } from "react";
import AppContainer from "../../common/app_container";
import DataTable from "react-data-table-component";
import { useProductAPI } from "@src/api/product_api";
import { IndustryType, Product } from "@src/model/product";
import { useNavigate } from "react-router-dom";
import Alert, { AlertProps } from "../../common/alert";
import _ from "lodash";

interface ProductIndexProps {}

const ProductIndex: React.FC<ProductIndexProps> = () => {
  const [data, setData] = React.useState<Product[]>([]);
  const [search, setSearch] = React.useState("");
  const [hideInactive, setHideInactive] = React.useState<boolean>(true);
  const [status, setStatus] = React.useState<AlertProps | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);
  const productAPI = useProductAPI();
  const navigate = useNavigate();

  const columns = [
    {
      name: "Name",
      selector: (row: Product) => row.name || "",
      sortable: true,
      width: "200px",
    },
    {
      name: "Industry Type",
      selector: (row: Product) => row.industry_type === IndustryType.OTHER ? "other [default]" : row.industry_type || "N/A",
      sortable: true,
      width: "150px",
    },
    
    {
      name: "Trial Days",
      selector: (row: Product) => row.trial_period_days || 0,
      sortable: true,
      width: "100px",
    },
    {
      name: "Active",
      selector: (row: Product) => row.is_active ? 1 : 0,
      sortable: true,
      cell: (row: Product) =>
        row.is_active ? (
          <span className="text-green-500">Active</span>
        ) : (
          <span className="text-red-500">Inactive</span>
        ),
      width: "100px",
    },
    {
      name: "Stripe Product ID",
      selector: (row: Product) => row.stripe_product_id || "",
      sortable: true,
      width: "200px",
      cell: (row: Product) =>
        row.stripe_info ? (
          <span className="text-green-500">{row.stripe_product_id}</span>
        ) : (
          <span className="text-red-500">{row.stripe_product_id}!!!</span>
        ),
    },
    {
      name: "Actions",
      cell: (row: Product) => (
        <div className="flex gap-2 flex-nowrap">
          <button
            onClick={() => navigate(`/products/edit/${row.id}`)}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-2 rounded"
          >
            Edit
          </button>
          <button
            onClick={() => handleDelete(row.id!)}
            className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-2 rounded"
          >
            Delete
          </button>
        </div>
      ),
      width: "180px",
    },
  ];

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        await productAPI.deleteProduct(id);
        setStatus({ type: "success", message: "Product deleted successfully" });
        debouncedFetchProducts(search);
      } catch (error: any) {
        setStatus({ type: "error", message: "Failed to delete product: " + error.message });
      }
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetchProducts = useCallback(
    _.debounce((searchTerm: string) => {
      setLoading(true);
      // When hideInactive is true, we only want active products
      const activeFilter = hideInactive ? true : undefined;
      productAPI
        .getProducts(true,searchTerm, activeFilter, undefined, 1000, 0)
        .then((response) => {
          setData(response);
          setLoading(false);
        })
        .catch((error) => {
          setStatus({ type: "error", message: "Failed to load products: " + error.message });
          setLoading(false);
        });
    }, 500),
    [productAPI, hideInactive]
  );

  useEffect(() => {
    debouncedFetchProducts(search);
    return () => {
      debouncedFetchProducts.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, hideInactive]);

  return (
    <AppContainer>
      <div className="container">
        <div className="flex justify-between">
          <h1 className="text-3xl">Products</h1>
          <button
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
            onClick={() => navigate("/products/add")}
          >
            Add Product
          </button>
        </div>
        {status && (
          <div className="mb-4">
            <Alert {...status} onClose={() => setStatus(null)} />
          </div>
        )}
        <form className="w-full mx-auto mt-2">
          <label
            htmlFor="default-search"
            className="mb-2 text-sm font-medium text-gray-900 sr-only dark:text-white"
          >
            Search
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
              <svg
                className="w-4 h-4 text-gray-500 dark:text-gray-400"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 20 20"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
                />
              </svg>
            </div>
            <input
              type="search"
              id="default-search"
              onChange={(val) => setSearch(val.target.value)}
              className="block w-full p-4 ps-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              placeholder="Search products by name..."
              required
            />
          </div>
          <div className="w-full flex justify-end gap-2 mt-2 items-center">
            <input
              id="hide-inactive"
              type="checkbox"
              checked={hideInactive}
              onChange={(e) => setHideInactive(e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
            />
            <label
              htmlFor="hide-inactive"
              className="text-sm font-medium text-gray-900 dark:text-gray-300"
            >
              Is Active
            </label>
          </div>
        </form>
        <DataTable
          columns={columns}
          data={data}
          pagination
          progressPending={loading}
          progressComponent={<div className="py-6">Loading products...</div>}
          noDataComponent={<div className="py-6">No products found</div>}
        />
      </div>
    </AppContainer>
  );
};

export default ProductIndex;

